import { useCallback, useEffect, useRef, useState } from 'react';
import { tokenKeyIntent, tokenRanges } from '../../TokenText/tokens';
import { clickHitsTokenText, createTokenBoundsAnchor, insertTokenIntoQuill, restoreTriggerBraces, stripTokenEmbeds } from './editorTokens';
const USER = 'user';
const CLOSED = {
    anchorEl: null,
    at: null
};
export function useTokenizedQuill(param) {
    let { editorEl, getQuill, tokens, quillReady } = param;
    const [{ anchorEl, at }, setTrigger] = useState(CLOSED);
    const anchorCleanupRef = useRef(null);
    const releaseAnchor = useCallback(()=>{
        var _anchorCleanupRef_current;
        (_anchorCleanupRef_current = anchorCleanupRef.current) === null || _anchorCleanupRef_current === void 0 ? void 0 : _anchorCleanupRef_current.call(anchorCleanupRef);
        anchorCleanupRef.current = null;
    }, []);
    const insertToken = useCallback((label)=>{
        const quill = getQuill();
        if (quill) insertTokenIntoQuill({
            quill,
            label
        });
    }, [
        getQuill
    ]);
    const select = useCallback((label)=>{
        const quill = getQuill();
        setTrigger(CLOSED);
        releaseAnchor();
        if (!quill) return;
        if (at != null) quill.setSelection(at, 0, USER);
        insertTokenIntoQuill({
            quill,
            label
        });
    }, [
        at,
        getQuill,
        releaseAnchor
    ]);
    const dismiss = useCallback(()=>{
        const quill = getQuill();
        setTrigger(CLOSED);
        releaseAnchor();
        if (quill && at != null) restoreTriggerBraces(quill, at);
    }, [
        at,
        getQuill,
        releaseAnchor
    ]);
    const openFrom = useCallback((element)=>{
        releaseAnchor();
        setTrigger({
            anchorEl: element,
            at: null
        });
    }, [
        releaseAnchor
    ]);
    useEffect(()=>{
        if (!tokens || !editorEl) return;
        const openTriggerAt = (quill, position)=>{
            var _ref, _ref1;
            quill.deleteText(position, 1, USER);
            releaseAnchor();
            const created = createTokenBoundsAnchor({
                editorEl,
                quill,
                index: position
            });
            anchorCleanupRef.current = (_ref = created === null || created === void 0 ? void 0 : created.cleanup) !== null && _ref !== void 0 ? _ref : null;
            setTrigger({
                anchorEl: (_ref1 = created === null || created === void 0 ? void 0 : created.anchor) !== null && _ref1 !== void 0 ? _ref1 : editorEl,
                at: position
            });
        };
        const handleKeyDown = (event)=>{
            const quill = getQuill();
            if (!quill) return;
            if (event.key === 'Escape' && anchorEl != null) {
                event.preventDefault();
                event.stopPropagation();
                dismiss();
                return;
            }
            const selection = quill.getSelection();
            if (!selection) return;
            const intent = tokenKeyIntent({
                text: quill.getText(),
                key: event.key,
                selectionStart: selection.index,
                selectionEnd: selection.index + selection.length,
                shiftKey: event.shiftKey,
                modifierKey: event.ctrlKey || event.metaKey,
                pattern: tokens.pattern
            });
            if (!intent) return;
            event.preventDefault();
            if (intent.type === 'moveCaret') quill.setSelection(intent.to, 0, USER);
            if (intent.type === 'select') quill.setSelection(intent.start, intent.end - intent.start, USER);
            if (intent.type === 'openTrigger') openTriggerAt(quill, intent.at);
        };
        editorEl.addEventListener('keydown', handleKeyDown);
        return ()=>editorEl.removeEventListener('keydown', handleKeyDown);
    }, [
        anchorEl,
        editorEl,
        getQuill,
        tokens,
        dismiss,
        releaseAnchor
    ]);
    useEffect(()=>{
        if (!tokens || !editorEl) return;
        const handleClick = (event)=>{
            const quill = getQuill();
            if (!quill) return;
            const editorRect = editorEl.getBoundingClientRect();
            const clickX = event.clientX - editorRect.left;
            const clickY = event.clientY - editorRect.top;
            const text = quill.getText();
            for (const { start, end, label } of tokenRanges(text, tokens.pattern)){
                var _ref, _ref1;
                var _tokens_onTokenClick;
                const length = end - start;
                if (!clickHitsTokenText({
                    quill,
                    start,
                    length,
                    clickX,
                    clickY
                })) continue;
                quill.setSelection(start, length, USER);
                releaseAnchor();
                const created = createTokenBoundsAnchor({
                    editorEl,
                    quill,
                    index: start,
                    length
                });
                anchorCleanupRef.current = (_ref = created === null || created === void 0 ? void 0 : created.cleanup) !== null && _ref !== void 0 ? _ref : null;
                (_tokens_onTokenClick = tokens.onTokenClick) === null || _tokens_onTokenClick === void 0 ? void 0 : _tokens_onTokenClick.call(tokens, {
                    label,
                    anchorEl: (_ref1 = created === null || created === void 0 ? void 0 : created.anchor) !== null && _ref1 !== void 0 ? _ref1 : editorEl
                });
                return;
            }
        };
        editorEl.addEventListener('click', handleClick);
        return ()=>editorEl.removeEventListener('click', handleClick);
    }, [
        editorEl,
        getQuill,
        tokens,
        releaseAnchor
    ]);
    useEffect(()=>{
        const quill = quillReady ? getQuill() : null;
        if (tokens && quill) stripTokenEmbeds(quill);
    }, [
        getQuill,
        quillReady,
        tokens
    ]);
    useEffect(()=>{
        if (!(tokens === null || tokens === void 0 ? void 0 : tokens.triggerRef)) return;
        const ref = tokens.triggerRef;
        ref.current = openFrom;
        return ()=>{
            ref.current = null;
        };
    }, [
        openFrom,
        tokens
    ]);
    useEffect(()=>releaseAnchor, [
        releaseAnchor
    ]);
    return {
        anchorEl,
        insertToken,
        select,
        dismiss,
        openFrom
    };
}
